import { useContext, useEffect, useState } from "react";
import { AppError } from "../../../../stores/models/errors/AppError";
import { OrganizationSignUpTermsOfServiceStepValues } from "../_interfaces/IOrganizationSignUpStepTypeValues";
import { IOrganizationSignUpFlowValues } from "../_interfaces/IOrganizationSignUpFlowValues";
import { ITermsOfServiceFormValues } from "../../../authentication/_interfaces/ITermsOfServiceFormValues";
import { OrganizationSignUpTermsOfServiceValidation } from "../validation/TermsOfServiceValidation";
import { RootContext } from "../../../../stores";
import { OrganizationType, StubOrganization } from "../../../../stores/models/StubOrganization";
import { AppErrors } from "../../../../stores/models/constants/AppErrors";

export function useOrganizationSignUpTermsOfServiceController(
    values: OrganizationSignUpTermsOfServiceStepValues,
    allValues: IOrganizationSignUpFlowValues,
    update: (patch: Partial<OrganizationSignUpTermsOfServiceStepValues>) => void,
    onSuccess: (override?: Partial<OrganizationSignUpTermsOfServiceStepValues>) => void
): {
    values: ITermsOfServiceFormValues,
    onChange: (patch: Partial<ITermsOfServiceFormValues>) => void,
    onSubmit: (override?: Partial<OrganizationSignUpTermsOfServiceStepValues>) => void,
    isSubmitting: boolean,
    error: string | undefined,
    fieldErrors: Partial<Record<keyof ITermsOfServiceFormValues, string>>
} {
    const rootStore = useContext(RootContext);

    const [error, setError] = useState<string>();
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ITermsOfServiceFormValues, string>>>({});
    const [isSubmitting, setSubmitting] = useState(false);
    const [validationRun, setValidationRun] = useState(false);

    useEffect(() => {
        update({ backClicked: false, termsOfServiceAccepted: false })
    }, [])

    useEffect(() => {
        if (validationRun) {
            const errors = validate().errors;
            setFieldErrors(errors);
        }
    }, [values.termsOfServiceAccepted, validationRun])


    const validate = (override?: Partial<OrganizationSignUpTermsOfServiceStepValues>) => {
        return OrganizationSignUpTermsOfServiceValidation({ ...values, ...override });
    }

    const isValidSubmission = (override?: Partial<OrganizationSignUpTermsOfServiceStepValues>) => {
        return validate(override).valid;
    }

    const submit = async (override?: Partial<OrganizationSignUpTermsOfServiceStepValues>) => {
        setSubmitting(true);
        setError(undefined);
        setValidationRun(true);

        try {
            const shouldSwitchStepsWithoutValidation = values.backClicked || override?.backClicked;
            if (shouldSwitchStepsWithoutValidation) { // If the flow should move to a different step
                update({ ...override });
                onSuccess(override);
            } else if (isValidSubmission(override)) {
                const effectiveValues = {
                    ...allValues,
                    ...override
                };
                const stubOrganization = new StubOrganization();
                stubOrganization.setOrganizationType(effectiveValues.isForProfit ? OrganizationType.Forprofit : OrganizationType.Nonprofit);
                if (effectiveValues.termsOfServiceAccepted && effectiveValues.organizationName && effectiveValues.ein && effectiveValues.signUpCode && effectiveValues.mission && effectiveValues.description) {
                    stubOrganization.setName(effectiveValues.organizationName);
                    stubOrganization.setEIN(effectiveValues.ein);
                    stubOrganization.setSignUpCode(effectiveValues.signUpCode);
                    stubOrganization.setMission(effectiveValues.mission);
                    stubOrganization.setDescription(effectiveValues.description);
                    stubOrganization.setWebsite(effectiveValues.website || "");
                } else {
                    throw "Missing property"
                }
                update({ loading: true });
                const result = await rootStore.userStore.registerOrganization(stubOrganization.serializedData, effectiveValues.termsOfServiceAccepted);
                update({ ...override, loading: false });
                onSuccess(override);
            }
        } catch (err) {
            if (err instanceof AppError) {
                switch (err.code) {
                    case AppErrors.CODE_INVALID.code:
                        setError("The supplied sign up code is invalid.")
                        break;
                    case AppErrors.CODE_USED.code:
                        setError("The supplied sign up code has already been used.")
                        break;
                    case AppErrors.CODE_EXPIRED.code:
                        setError("The supplied sign up code has expired. Please request a new one.")
                        break;
                    default:
                        setError(err.message);
                }
            } else {
                setError("An unknown error occurred.");
            }
        } finally {
            update({ loading: false });
            setSubmitting(false);
        }
    };

    return {
        values: {
            ...values,
            buttonText: 'Submit'
        },
        onChange: update,
        onSubmit: submit,
        isSubmitting,
        error,
        fieldErrors
    };
}